import { PieceDto } from "@growth/dto";
import { SlideInput } from "@growth/schema";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import { Download, FileText, LoaderCircle, Pencil, Save, X } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ErrorText } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useEditSlides } from "../lib/content";
import { isTall } from "./content";

type Carousel = NonNullable<PieceDto["carousel"]>;
type TextField = Exclude<keyof SlideInput, "layout" | "sentences" | "items">;

/** The words a person can change on each layout (the template owns everything else). */
const FIELDS: Record<SlideInput["layout"], { key: TextField; label: string; long?: boolean }[]> = {
  cover: [
    { key: "eyebrow", label: "Eyebrow" },
    { key: "title", label: "Title", long: true },
    { key: "subtitle", label: "Subtitle", long: true },
  ],
  point: [
    { key: "heading", label: "Heading" },
    { key: "body", label: "Body", long: true },
  ],
  markup: [
    { key: "eyebrow", label: "Eyebrow" },
    { key: "question", label: "Question", long: true },
    { key: "keepNote", label: "Keep note" },
    { key: "changeNote", label: "Change note" },
  ],
  compare: [
    { key: "heading", label: "Heading" },
    { key: "weak", label: "Weak", long: true },
    { key: "strong", label: "Strong", long: true },
  ],
  checklist: [
    { key: "eyebrow", label: "Eyebrow" },
    { key: "heading", label: "Heading" },
  ],
  quote: [
    { key: "quote", label: "Quote", long: true },
    { key: "attribution", label: "Attribution" },
  ],
  cta: [
    { key: "title", label: "Title" },
    { key: "body", label: "Body", long: true },
    { key: "action", label: "Button" },
  ],
};

const LAYOUT_LABELS: Record<SlideInput["layout"], string> = {
  cover: "Cover",
  point: "Point",
  markup: "Keep / change",
  compare: "Weak vs strong",
  checklist: "Checklist",
  quote: "Example",
  cta: "Call to action",
};

/** The rendered slides, their files, and (for editors) a text editor that re-renders them. */
export const Slides = ({ piece, carousel }: { piece: PieceDto; carousel: Carousel }) => {
  const { atLeast } = useActive();
  const [editing, setEditing] = useState(false);
  const editable =
    atLeast("EDITOR") &&
    !carousel.rendering &&
    ["awaiting_approval", "needs_human", "blocked", "failed"].includes(piece.status);

  return (
    <div className="space-y-4 rounded-xl border border-line bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg text-ink">The slides</h2>
          <p className="text-sm text-muted">{carousel.docTitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={carousel.zipUrl}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-ink hover:bg-paper"
          >
            <Download className="size-4" /> Slides (zip)
          </a>
          <a
            href={`${carousel.pdfUrl}?download`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-sm text-ink hover:bg-paper"
          >
            <FileText className="size-4" /> PDF
          </a>
          {editable && !editing && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="size-4" /> Edit slides
            </Button>
          )}
        </div>
      </div>

      {carousel.rendering && (
        <p className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-ink">
          <LoaderCircle className="size-4 animate-spin" /> Re-rendering your edit and re-checking
          it…
        </p>
      )}
      {carousel.renderError && (
        <ErrorText>Your last slide edit didn't apply: {carousel.renderError}</ErrorText>
      )}
      {carousel.layoutProblems.length > 0 && (
        <ul className="space-y-1 text-sm text-amber-700">
          {carousel.layoutProblems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      {editing ? (
        <SlideEditor carousel={carousel} pieceId={piece.id} onDone={() => setEditing(false)} />
      ) : (
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          {carousel.slides.map((slide, index) => (
            <li key={slide.url} className="space-y-1.5">
              <a href={slide.url} target="_blank" rel="noreferrer" className="block">
                <img
                  src={slide.url}
                  alt={slide.altText}
                  loading="lazy"
                  className={`${isTall(piece.channel) ? "aspect-[9/16]" : "aspect-[4/5]"} w-full rounded-md border border-line bg-paper object-cover`}
                />
              </a>
              <p className="text-xs text-muted">
                <span className="font-medium text-ink">{index + 1}.</span> {slide.altText}
              </p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const SlideEditor = ({
  carousel,
  pieceId,
  onDone,
}: {
  carousel: Carousel;
  pieceId: string;
  onDone: () => void;
}) => {
  const save = useEditSlides(pieceId);
  const [slides, setSlides] = useState<SlideInput[]>(carousel.spec);
  const [altText, setAltText] = useState(carousel.slides.map((slide) => slide.altText));

  const update = (index: number, change: Partial<SlideInput>) =>
    setSlides((current) =>
      current.map((slide, i) => (i === index ? { ...slide, ...change } : slide)),
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const cleaned = slides.map((slide) =>
      slide.items
        ? { ...slide, items: slide.items.map((item) => item.trim()).filter(Boolean) }
        : slide,
    );
    save.mutate({ slides: cleaned, altText }, { onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-muted">
        Change the words; the layout, type and colours stay with the template. Saving re-renders the
        slides and re-runs the claims check.
      </p>
      <ol className="space-y-3">
        {slides.map((slide, index) => (
          <li
            key={index}
            className="grid gap-4 rounded-lg border border-line p-3 sm:grid-cols-[8rem_1fr]"
          >
            <div className="space-y-1">
              <img
                src={carousel.slides[index]?.url}
                alt=""
                className="aspect-[4/5] w-full rounded border border-line object-cover"
              />
              <Badge>
                {index + 1}. {LAYOUT_LABELS[slide.layout]}
              </Badge>
            </div>
            <div className="min-w-0 space-y-2">
              {FIELDS[slide.layout].map(({ key, label, long }) => (
                <Field key={key} label={label}>
                  {(props) =>
                    long ? (
                      <Textarea
                        {...props}
                        rows={2}
                        value={slide[key] ?? ""}
                        onChange={(event) => update(index, { [key]: event.target.value || null })}
                      />
                    ) : (
                      <Input
                        {...props}
                        value={slide[key] ?? ""}
                        onChange={(event) => update(index, { [key]: event.target.value || null })}
                      />
                    )
                  }
                </Field>
              ))}
              {slide.layout === "checklist" && (
                <Field label="Items (one per line)">
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={4}
                      value={(slide.items ?? []).join("\n")}
                      onChange={(event) => update(index, { items: event.target.value.split("\n") })}
                    />
                  )}
                </Field>
              )}
              {slide.layout === "markup" &&
                (slide.sentences ?? []).map((sentence, s) => (
                  <Field
                    key={s}
                    label={`Sentence ${s + 1}${sentence.role === "plain" ? "" : ` (${sentence.role})`}`}
                  >
                    {(props) => (
                      <Input
                        {...props}
                        value={sentence.text}
                        onChange={(event) =>
                          update(index, {
                            sentences: (slide.sentences ?? []).map((item, i) =>
                              i === s ? { ...item, text: event.target.value } : item,
                            ),
                          })
                        }
                      />
                    )}
                  </Field>
                ))}
              <Field label="Alt text">
                {(props) => (
                  <Input
                    {...props}
                    maxLength={1000}
                    value={altText[index] ?? ""}
                    onChange={(event) =>
                      setAltText((current) =>
                        current.map((text, i) => (i === index ? event.target.value : text)),
                      )
                    }
                  />
                )}
              </Field>
            </div>
          </li>
        ))}
      </ol>
      {save.isError && <ErrorText>{apiErrorMessage(save.error)}</ErrorText>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Save and re-render
        </Button>
        <Button type="button" variant="secondary" onClick={onDone} disabled={save.isPending}>
          <X className="size-4" /> Cancel
        </Button>
      </div>
    </form>
  );
};
